import { useDispatch, useSelector } from 'react-redux'

// antd
import {
    Avatar,
    Image,
    Row,
    Table,
    Typography,
    theme,
    Badge,
    Dropdown,
    Pagination,
    Tooltip,
    Drawer,
    Modal,
    Spin,
} from 'antd'
import { EllipsisOutlined } from '@ant-design/icons'

// slices
import {
    changePage,
    clearTalentDetail,
    getTalentDetails,
    talentDetailGet,
    talentListGet,
    talentState,
    updateTalent,
} from './talent.slice'
import { updateUserGet } from '../profile/profile.slice'

// assets
import {
    AssignIcon,
    NonVerifyTalentIcon,
    UnAssignIcon,
    VerifyTalentIcon,
    BypassIcon,
    ChangeCostIcon,
    ViewIcon,
} from '../../assets/icons'

// constants
import { TALENT_FILTER_STATUS } from '../../constants/general'
import { DOMAIN_LIST } from '../../constants/roles'
import { EditPermissionIcon } from '../../assets/icons/inquiries'
import { useState } from 'react'
import EditPermission from './talentMenu/EditPermission'

const TalentList = ({ props = {} }) => {
    const {
        talentView,
        modalOpen,
        unAssignModalOpen,
        addRatesModalOpen,
        talentLogin,
        updateStatus,
        talentModalOpen,
    } = props
    const { Text, Title } = Typography

    const dispatch = useDispatch()

    const { useToken } = theme

    const { token } = useToken()

    const [isModalOpen, setIsModalOpen] = useState(false)
    const [selectedUser, setSelectedUser] = useState(null)
    const [statusLoading, setStatusLoading] = useState(false)

    const handleChangeStatus = async () => {
        setStatusLoading(true)
        const payload = {
            updatedData: {
                data: {
                    isActive: selectedUser?.isActive ? false : true,
                },
            },
            _id: selectedUser?._id,
        }
        const result = await dispatch(updateTalent(payload))
        if (result?.payload?.data.success) {
            dispatch(
                talentListGet({
                    limit: 10,
                    skip: 0,
                    search: search,
                    filter: { ...filter, status: TALENT_FILTER_STATUS },
                })
            )
            setStatusLoading(false)
            setIsModalOpen(false)
        }
    }

    const handleCancel = () => {
        setIsModalOpen(false)
    }

    const {
        pageLimit,
        currentPage,
        dataCount,
        isNext,
        talentList,
        filter,
        search,
        selectedData,
    } = useSelector(talentState)

    const [permissionDrawerOpen, setPermissionDrawerOpen] = useState(false)

    // COLUMNS
    const columns = [
        {
            title: 'Name',
            key: 'name',
            dataIndex: 'name',
            render: (val, item) => (
                <Row
                    align={'middle'}
                    style={{
                        columnGap: '16px',
                    }}
                >
                    {item?.profilePicture ? (
                        <Image
                            src={item?.profilePicture}
                            height={'30px'}
                            preview={false}
                            width={'30px'}
                            style={{
                                borderRadius: '50%',
                            }}
                        />
                    ) : (
                        <Avatar
                            style={{
                                cursor: 'pointer',
                                backgroundColor: 'rgb(0 10 129 / 25%)',
                            }}
                        >
                            {item?.name[0].toUpperCase()}
                            {item?.name.split(' ')[1] !== undefined &&
                                item?.name.split(' ')[1] !== '' &&
                                item?.name.split(' ')[1][0].toUpperCase()}
                        </Avatar>
                    )}
                    <Text>{val}</Text>
                </Row>
            ),
        },
        {
            key: 'email',
            dataIndex: 'email',
            title: 'Email ID',
            render: (val) => (
                <div style={{ fontWeight: 500 }}>{val || 'NA'}</div>
            ),
        },
        {
            key: 'phone',
            dataIndex: 'phone',
            title: 'Mobile Number',
            render: (val, item) => (
                <div style={{ fontWeight: 500 }}>
                    {item?.countryCode}
                    {val || 'NA'}
                </div>
            ),
        },
        {
            title: 'Workspace',
            key: 'workspace',
            dataIndex: 'workspace',
            render: (_, item) =>
                item?.workspace?.length > 1 ? (
                    <Row
                        align="middle"
                        style={{
                            columnGap: '0px', // No gap between images
                            flexWrap: 'nowrap', // Ensure all items stay in one line
                            position: 'relative', // Positioning for overlap
                            overflow: 'visible', // Ensure container doesn't clip images
                        }}
                    >
                        {item?.workspace
                            ?.filter((w) => !DOMAIN_LIST.includes(w?.domain))
                            ?.map((w, index) => (
                                <Tooltip
                                    title={w?.companyName}
                                    key={w?.companyName}
                                >
                                    <Image
                                        src={w.logo}
                                        height="30px"
                                        preview={false}
                                        width="30px"
                                        style={{
                                            borderRadius: '50%',
                                            objectFit: 'contain',
                                            border: '2px solid #d9d9d9', // Gray border
                                            cursor: 'pointer', // Pointer cursor on hover
                                            marginLeft:
                                                index === 0
                                                    ? '0'
                                                    : `-${10 * index}px`, // Apply negative margin for overlap
                                            zIndex:
                                                item.workspace.length - index, // Ensure correct stacking order
                                        }}
                                    />
                                </Tooltip>
                            ))}
                    </Row>
                ) : (
                    <Text
                        style={{
                            color: token.colorPalette.baseColor.error,
                        }}
                    >
                        Unassigned
                    </Text>
                ),
        },
        {
            key: 'isOurUser',
            dataIndex: 'isOurUser',
            title: 'Verification',
            render: (item) => (
                <Text
                    style={{
                        color: item
                            ? token.colorPalette.baseColor.tertiary
                            : token.colorPalette.baseColor.error,
                    }}
                >
                    {item ? 'Yes' : 'No'}
                </Text>
            ),
        },
        {
            key: 'isActive',
            dataIndex: 'isActive',
            title: 'Status',
            render: (item, record) => {
                const assignedWorkspaces = record.workspace?.filter(
                    (w) => !DOMAIN_LIST.includes(w?.domain)
                )
                const hasWorkspace = assignedWorkspaces?.length > 0
                const isBlockDeactivation = record.isActive && hasWorkspace

                return (
                    <Tooltip
                        title={
                            isBlockDeactivation
                                ? 'Please unassign workspace before deactivating the user.'
                                : ''
                        }
                    >
                        <Badge
                            count={item ? 'Active' : 'Inactive'}
                            color={
                                item
                                    ? 'hsla(155, 54%, 46%, 0.16)'
                                    : 'hsla(11, 100%, 59%, 0.16)'
                            }
                            onClick={() => {
                                if (isBlockDeactivation) {
                                    return // Tooltip already shown
                                }
                                setSelectedUser(record)
                                setIsModalOpen(true)
                            }}
                            style={{
                                minWidth: '30px',
                                cursor: isBlockDeactivation
                                    ? 'not-allowed'
                                    : 'pointer',
                                borderRadius: '6px',
                                display: 'flex',
                                alignItems: 'center',
                                color: item
                                    ? token.colorPalette.iconColor.tertiary
                                    : token.colorPalette.iconColor.quaternary,
                                padding: '12px 8px',
                                fontWeight: 700,
                            }}
                        />
                    </Tooltip>
                )
            },
        },
        {
            key: 'context',
            dataIndex: 'context',
            title: '  ',
            render: (_, row) => (
                <Row justify="end" style={{ paddingRight: 5 }}>
                    <Dropdown
                        trigger={['click']}
                        placement={'bottomRight'}
                        menu={{
                            items: [
                                {
                                    key: '1',
                                    label: 'View',
                                    icon: <ViewIcon />,
                                    style: {
                                        columnGap: '10px',
                                    },
                                    onClick: () => {
                                        dispatch(
                                            updateUserGet({
                                                _id: row?._id,
                                            })
                                        )
                                        talentModalOpen()
                                    },
                                },
                                row?.isActive &&
                                    row?.phone && {
                                        key: '2',
                                        label: 'Login',
                                        icon: <BypassIcon />,
                                        style: {
                                            columnGap: '10px',
                                        },
                                        onClick: () => {
                                            talentLogin(row)
                                        },
                                    },
                                {
                                    key: '3',
                                    style: {
                                        columnGap: '10px',
                                        cursor:
                                            row?.workspace?.length === 1 ||
                                            (row?.isActive && row?.isOurUser)
                                                ? 'pointer'
                                                : 'no-drop',
                                    },
                                    icon: (
                                        <div
                                            style={{
                                                width: '20px',
                                                height: '20px',
                                                margin: '0px',
                                            }}
                                        >
                                            <AssignIcon
                                                color={
                                                    (!row?.isActive ||
                                                        !row?.isOurUser) &&
                                                    token.colorPalette.textColor
                                                        .senary
                                                }
                                            />
                                        </div>
                                    ),
                                    label: (
                                        <Text
                                            style={{
                                                color:
                                                    row?.isActive &&
                                                    row?.isOurUser
                                                        ? token.colorPalette
                                                              .baseColor.black
                                                        : token.colorPalette
                                                              .textColor.senary,
                                            }}
                                        >
                                            Workspace Assign
                                        </Text>
                                    ),
                                    onClick: () => {
                                        dispatch(getTalentDetails(row))
                                        if (
                                            row?.isActive &&
                                            row?.isOurUser
                                            // &&
                                            // !row?.isWorkspaceAssigned
                                        ) {
                                            modalOpen()
                                        }
                                    },
                                },
                                row?.isWorkspaceAssigned && {
                                    key: '4',
                                    style: {
                                        columnGap: '10px',
                                    },
                                    icon: (
                                        <div
                                            style={{
                                                width: '20px',
                                                height: '20px',
                                                margin: '0px',
                                            }}
                                        >
                                            <div
                                                style={{
                                                    height: '17px',
                                                    width: '17px',
                                                }}
                                            >
                                                <UnAssignIcon />
                                            </div>
                                        </div>
                                    ),
                                    label: <Text>Workspace UnAssign</Text>,
                                    onClick: () => {
                                        dispatch(getTalentDetails(row))
                                        unAssignModalOpen()
                                    },
                                },
                                row?.isOurUser &&
                                    !row?.isWorkspaceAssigned && {
                                        key: '5',
                                        style: {
                                            columnGap: '10px',
                                        },
                                        icon: (
                                            <div
                                                style={{
                                                    width: '20px',
                                                    height: '20px',
                                                    margin: '0px',
                                                }}
                                            >
                                                <NonVerifyTalentIcon />
                                            </div>
                                        ),
                                        label: (
                                            <Text
                                                style={{
                                                    color: token.colorPalette
                                                        .baseColor.error,
                                                }}
                                            >
                                                {'Not Verified'}
                                            </Text>
                                        ),
                                        onClick: () => {
                                            updateStatus(
                                                row?._id,
                                                row?.isOurUser
                                            )
                                        },
                                    },
                                !row?.isOurUser && {
                                    key: '5',
                                    style: {
                                        columnGap: '10px',
                                    },
                                    icon: (
                                        <div
                                            style={{
                                                width: '20px',
                                                height: '20px',
                                                margin: '0px',
                                            }}
                                        >
                                            <VerifyTalentIcon />
                                        </div>
                                    ),
                                    label: (
                                        <Text
                                            style={{
                                                color: token.colorPalette
                                                    .iconColor.tertiary,
                                            }}
                                        >
                                            {'Verified'}
                                        </Text>
                                    ),
                                    onClick: () => {
                                        updateStatus(row?._id, row?.isOurUser)
                                    },
                                },
                                {
                                    key: '6',
                                    style: {
                                        columnGap: '10px',
                                    },
                                    icon: (
                                        <div
                                            style={{
                                                width: '20px',
                                                height: '20px',
                                                margin: '0px',
                                            }}
                                        >
                                            <ChangeCostIcon />
                                        </div>
                                    ),
                                    label: <Text>Change Rates</Text>,
                                    onClick: () => {
                                        dispatch(getTalentDetails(row))
                                        addRatesModalOpen()
                                    },
                                },
                                {
                                    key: '7',
                                    style: {
                                        columnGap: '10px',
                                    },
                                    icon: (
                                        <div
                                            style={{
                                                width: '17px',
                                                height: '17px',
                                                margin: '0px',
                                            }}
                                        >
                                            <EditPermissionIcon />
                                        </div>
                                    ),
                                    label: <Text>Edit Permissions</Text>,
                                    onClick: () => {
                                        dispatch(getTalentDetails(row))
                                        setPermissionDrawerOpen(true)
                                        dispatch(talentDetailGet(row?._id))
                                    },
                                },
                            ],
                        }}
                    >
                        <EllipsisOutlined rotate={90} />
                    </Dropdown>
                </Row>
            ),
        },
    ]

    const onCloseDrawer = () => {
        setPermissionDrawerOpen(false)
        dispatch(clearTalentDetail())
    }

    return (
        <>
            <Drawer
                placement="right"
                size={'default'}
                style={{
                    height: '100vh',
                    minWidth: '500px',
                    width: '500px',
                }}
                styles={{
                    body: {
                        padding: '0px',
                        display: 'flex',
                        flexDirection: 'column',
                    },
                    wrapper: {
                        width: 'auto !important',
                    },
                }}
                rootClassName="appPermissionDrawer"
                onClose={onCloseDrawer}
                open={permissionDrawerOpen}
                closeIcon={null}
                title={
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 12,
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 12,
                            }}
                        >
                            {selectedData?.profilePicture ? (
                                <Image
                                    src={selectedData?.profilePicture}
                                    height={36}
                                    className="headerImage"
                                    width={36}
                                    style={{
                                        borderRadius: '50%',
                                        verticalAlign: 'baseline !important',
                                        cursor: 'pointer',
                                    }}
                                    preview={false}
                                />
                            ) : (
                                <Avatar
                                    size={'large'}
                                    style={{
                                        cursor: 'pointer',
                                        backgroundColor: 'rgb(0 10 129 / 25%)',
                                    }}
                                >
                                    {selectedData?.name[0].toUpperCase()}
                                    {selectedData?.name.split(' ')[1] !==
                                        undefined &&
                                        selectedData?.name
                                            .split(' ')[1][0]
                                            .toUpperCase()}
                                </Avatar>
                            )}
                            <div>
                                <Title level={5} style={{ margin: 0 }}>
                                    {selectedData?.name}
                                </Title>
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    {selectedData?.email}
                                </Text>
                            </div>
                        </div>
                    </div>
                }
            >
                <EditPermission />
            </Drawer>
            <Table
                dataSource={talentList}
                scroll={{
                    x: 'scroll',
                }}
                rootClassName="tableGlobal"
                style={{
                    borderRadius: '16px',
                }}
                columns={columns}
                pagination={false}
            />
            {talentList?.length > 0 && talentView === 'list' && (
                <Row
                    justify={'end'}
                    align={'middle'}
                    style={{
                        border: `2px solid ${token.colorPalette.baseColor.quaternary}`,
                        padding: '25px',
                        borderRadius: '0px 0px 8px 8px',
                    }}
                >
                    <Text>
                        {talentList?.length} - {pageLimit} of {dataCount}
                    </Text>
                    <Pagination
                        defaultCurrent={currentPage}
                        current={currentPage}
                        defaultPageSize={pageLimit}
                        pageSizeOptions={[5, 10, 20, 50]}
                        showSizeChanger={true}
                        total={
                            isNext === false && currentPage === 1
                                ? talentList?.length
                                : dataCount
                        }
                        onChange={async (value, pageSize) => {
                            await dispatch(
                                changePage({
                                    currentPage: value,
                                    skip: (value - 1) * pageLimit,
                                    pageLimit: pageSize,
                                })
                            )
                            await dispatch(
                                talentListGet({
                                    skip: (value - 1) * pageSize,
                                    limit: pageSize,
                                    filter: {
                                        ...filter,
                                        status: TALENT_FILTER_STATUS,
                                    },
                                    search,
                                })
                            )
                        }}
                    />
                </Row>
            )}
            <Modal
                // centered

                title={
                    <Row
                        align="middle"
                        justify="start"
                        style={{ columnGap: '10px' }}
                    >
                        <Title
                            level={4}
                            style={{ margin: 0, fontWeight: '700' }}
                        >
                            Change User Status
                        </Title>
                    </Row>
                }
                open={isModalOpen}
                onCancel={handleCancel}
                footer={[
                    <button
                        key="cancel"
                        onClick={handleCancel}
                        style={{
                            backgroundColor: 'transparent',
                            border: '1px solid #d9d9d9',
                            padding: '6px 16px',
                            borderRadius: '4px',
                            marginRight: '8px',
                            cursor: 'pointer',
                        }}
                    >
                        Cancel
                    </button>,
                    <button
                        key="confirm"
                        onClick={handleChangeStatus}
                        disabled={statusLoading}
                        style={{
                            backgroundColor: selectedUser?.isActive
                                ? '#ff542e29'
                                : '#36b58029',
                            color: selectedUser?.isActive
                                ? '#b71d18'
                                : '#1b806a',
                            padding: '6px 16px',
                            borderRadius: '4px',
                            border: 'none',
                            cursor: 'pointer',
                            border: '1px solid',
                            borderColor: selectedUser?.isActive
                                ? '#b71d1859'
                                : '#0240024a',
                        }}
                    >
                        {statusLoading && (
                            <Spin style={{ marginRight: '7px' }} size="small" />
                        )}
                        {selectedUser?.isActive ? 'Deactivate' : 'Activate'}
                    </button>,
                ]}
            >
                <p style={{ marginTop: '10px' }}>
                    Are you sure you want to{' '}
                    <b
                        style={{
                            color: selectedUser?.isActive
                                ? '#b71d18'
                                : '#1b806a',
                        }}
                    >
                        {selectedUser?.isActive ? 'deactivate' : 'activate'}
                    </b>{' '}
                    <b>{selectedUser?.name}</b>?
                    {selectedUser?.isActive
                        ? ' They will lose access to the platform.'
                        : ' This will allow them to access the platform.'}
                </p>
            </Modal>
        </>
    )
}

export default TalentList
